import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';
import { ListLoader } from '../../utils/loader';
import ConfirmationModal from '../../ui/confirmation-modal';

interface Claim {
  id: number;
  entity_type: 'team' | 'tournament';
  entity_id: number;
  entity_name: string | null;
  claimant: { id: number; phone_number: string | null; name: string | null };
  message: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

const statusStyle = (s: string) => {
  switch (s) {
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'approved': return 'bg-green-100 text-green-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const OwnershipClaims: React.FC = () => {
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [confirm, setConfirm] = useState<{ open: boolean; action: 'approve' | 'reject' | null; claim: Claim | null }>({ open: false, action: null, claim: null });

  // Direct admin override
  const [assign, setAssign] = useState({ entity_type: 'team', entity_id: '', user_id: '' });
  const [assigning, setAssigning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : '';
      const res: any = await api.get(`/ownership-claims${q}`);
      setClaims(res.data || []);
    } catch (e) {
      toast.error('Failed to load claims');
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const doAction = async () => {
    if (!confirm.claim || !confirm.action) return;
    try {
      await api.post(`/ownership-claims/${confirm.claim.id}/${confirm.action}`);
      toast.success(confirm.action === 'approve' ? 'Claim approved & ownership assigned' : 'Claim rejected');
      setConfirm({ open: false, action: null, claim: null });
      load();
    } catch (e) {
      toast.error('Action failed');
    }
  };

  const doAssign = async () => {
    if (!assign.entity_id || !assign.user_id) { toast.error('Entity ID and User ID are required'); return; }
    setAssigning(true);
    try {
      await api.post('/ownership-claims/assign', {
        entity_type: assign.entity_type,
        entity_id: Number(assign.entity_id),
        user_id: Number(assign.user_id),
      });
      toast.success('Ownership assigned');
      setAssign({ entity_type: 'team', entity_id: '', user_id: '' });
      load();
    } catch (e) {
      toast.error('Failed to assign ownership');
    } finally { setAssigning(false); }
  };

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Ownership Claims</h1>
          <p className="text-sm text-gray-500">Members claiming an orphaned team / tournament (previous owner deleted their account). Approve to assign them.</p>
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="">All</option>
        </select>
      </div>

      {loading ? <ListLoader /> : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Entity</th>
                  <th className="text-left font-medium px-4 py-3">Claimant</th>
                  <th className="text-left font-medium px-4 py-3">Message</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="text-right font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {claims.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No claims</td></tr>
                ) : claims.map((c) => (
                  <tr key={c.id} className="border-t border-gray-100">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{c.entity_name || `#${c.entity_id}`}</div>
                      <div className="text-xs text-gray-400 capitalize">{c.entity_type} · #{c.entity_id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900">{c.claimant.name || '-'}</div>
                      <div className="text-xs text-gray-400">{c.claimant.phone_number || `user #${c.claimant.id}`}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{c.message || '-'}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusStyle(c.status)}`}>{c.status}</span></td>
                    <td className="px-4 py-3 text-right">
                      {c.status === 'pending' ? (
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setConfirm({ open: true, action: 'approve', claim: c })} className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg">Approve</button>
                          <button onClick={() => setConfirm({ open: true, action: 'reject', claim: c })} className="bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg">Reject</button>
                        </div>
                      ) : <span className="text-xs text-gray-400">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Direct admin override */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mt-6 max-w-3xl">
        <h2 className="text-sm font-semibold text-gray-900 mb-1">Direct reassign (override)</h2>
        <p className="text-xs text-gray-400 mb-4">Manually set a new owner/organizer without a claim.</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
            <select value={assign.entity_type} onChange={(e) => setAssign((p) => ({ ...p, entity_type: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="team">Team</option>
              <option value="tournament">Tournament</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Entity ID</label>
            <input value={assign.entity_id} onChange={(e) => setAssign((p) => ({ ...p, entity_id: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">New owner user ID</label>
            <input value={assign.user_id} onChange={(e) => setAssign((p) => ({ ...p, user_id: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <button onClick={doAssign} disabled={assigning} className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-60">
            {assigning ? 'Assigning...' : 'Assign'}
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, action: null, claim: null })}
        onConfirm={doAction}
        title={confirm.action === 'approve' ? 'Approve claim' : 'Reject claim'}
        description={confirm.action === 'approve'
          ? `Assign ${confirm.claim?.claimant.name || 'this user'} as owner/organizer of ${confirm.claim?.entity_name || 'this entity'}?`
          : 'Reject this ownership claim?'}
      />
    </div>
  );
};

export default OwnershipClaims;
